import {
  DEFAULT_ACCOUNT_LABEL,
  type MnemonicAccount,
  defaultDerivationPathTemplates,
  normalizeSecretKey,
} from "@umami/chains";
import { useDynamicModalContext } from "@umami/components";
import {
  accountsActions,
  generate24WordMnemonic,
  useAppDispatch,
  useAsyncActionHandler,
  useCreateMnemonicWallets,
  useCreateSecretKeyWallets,
  useCurrentAccount,
  useGetDecryptedMnemonic,
  useGetNextAvailableAccountLabels,
  useRestoreFromSecretKey,
  useValidateMasterPassword,
} from "@umami/state";
import { isMultichainEnabled } from "@umami/utils";

import { type FormFields, type Mode } from "./types";
import { trackButtonClick } from "../../../utils/analytics";
import { setupPersistence } from "../../../utils/store";
import { ImportantNoticeModal } from "../VerificationFlow/ImportantNoticeModal";

const useGetSecretKeyHandler = () => {
  const { allFormValues } = useDynamicModalContext();
  const restoreFromSecretKey = useRestoreFromSecretKey();
  const createSecretKeyWallets = useCreateSecretKeyWallets();
  const dispatch = useAppDispatch();

  return async (password: string) => {
    const secretKey = allFormValues.current?.secretKey;
    const { tezos: tezosKey } = await normalizeSecretKey(secretKey);

    if (isMultichainEnabled()) {
      const account = await createSecretKeyWallets({
        secretKey,
        password,
        label: DEFAULT_ACCOUNT_LABEL,
      });

      dispatch(accountsActions.setCurrent(account.address.pkh));
    } else {
      await restoreFromSecretKey(tezosKey, password, DEFAULT_ACCOUNT_LABEL);
    }

    await setupPersistence(tezosKey);
  };
};

export const useGetMnemonicHandler = () => {
  const { allFormValues } = useDynamicModalContext();
  const createMnemonicWallets = useCreateMnemonicWallets();
  const getNextAvailableAccountLabels = useGetNextAvailableAccountLabels();
  const dispatch = useAppDispatch();

  const nextAvailableLabel = getNextAvailableAccountLabels(DEFAULT_ACCOUNT_LABEL)[0];

  return async (
    { password, curve, label, derivationPaths }: FormFields,
    isNewMnemonic: boolean
  ) => {
    const mnemonic = isNewMnemonic
      ? generate24WordMnemonic()
      : allFormValues.current?.mnemonic.map(({ val }: { val: string }) => val).join(" ");

    const accounts = await createMnemonicWallets({
      mnemonic,
      password,
      derivationPaths,
      derivationPathsTemplates: defaultDerivationPathTemplates,
      label: allFormValues.current?.accountName || label || nextAvailableLabel,
      curve,
      isVerified: !isNewMnemonic,
    });

    await setupPersistence(mnemonic);

    if (isNewMnemonic) {
      dispatch(accountsActions.setPassword(password));
    }
    dispatch(accountsActions.setCurrent(accounts[0].address.pkh));
  };
};

const useGetVerificationHandler = () => {
  const { openWith } = useDynamicModalContext();
  const getDecryptedMnemonic = useGetDecryptedMnemonic();
  const currentAccount = useCurrentAccount();

  return async (password: string) => {
    const mnemonic = await getDecryptedMnemonic(currentAccount as MnemonicAccount, password);

    return openWith(<ImportantNoticeModal mnemonic={mnemonic} />, { size: "xl" });
  };
};

export const useGetSetupPasswordSubmitHandler = (mode: Mode) => {
  const { onClose } = useDynamicModalContext();
  const { handleAsyncAction, isLoading } = useAsyncActionHandler();
  const checkPassword = useValidateMasterPassword();

  const handleVerify = useGetVerificationHandler();
  const handleSecretKey = useGetSecretKeyHandler();
  const handleMnemonic = useGetMnemonicHandler();

  const isNewMnemonic = mode === "new_mnemonic" || mode === "add_account";

  return {
    onSubmit: (formValues: FormFields) =>
      handleAsyncAction(async () => {
        const { password } = formValues;
        await checkPassword?.(password);

        switch (mode) {
          case "secret_key": {
            await handleSecretKey(password);
            trackButtonClick("create_password", {
              button_label: "Import wallet",
              section: "Welcome",
              flow: "Onboarding",
              action: "button_click",
            });
            break;
          }
          case "mnemonic":
          case "new_mnemonic":
          case "add_account": {
            if (mode === "new_mnemonic") {
              trackButtonClick("create_password", {
                button_label: "Create account",
                section: "Welcome",
                flow: "Onboarding",
                action: "button_click",
              });
            }

            if (mode === "mnemonic") {
              trackButtonClick("create_password", {
                button_label: "Import wallet",
                section: "Welcome",
                flow: "Onboarding",
                action: "button_click",
              });
            }

            await handleMnemonic(formValues, isNewMnemonic);
            break;
          }
          case "verification": {
            return handleVerify(password);
          }
          case "import_backup":
            // This case is intentionally left blank to prevent a TypeScript error.
            // It's needed to satisfy exhaustive checks elsewhere in the code,
            // due to tight coupling between nested logic structures.
            trackButtonClick("create_password", {
              button_label: "Import wallet",
              section: "Welcome",
              flow: "Onboarding",
              action: "button_click",
            });

            return;
        }

        onClose();
      }),
    isLoading,
  };
};
